// src/App.js

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useLocation, useParams } from "react-router-dom";
import { Activity, ClipboardCheck, ClipboardList, FileSpreadsheet, HeartPulse, Home, Hospital, Plus, Search, Users } from "lucide-react";

import { FormProgressProvider } from "./context/FormProgressContext";
import { ActiveFormSessionProvider } from "./context/ActiveFormSessionContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PatientProvider } from "./context/PatientContext";
import { useFormProgress } from "./context/FormProgressContext";
import { canSeeAnalytics, canSeeDataQueries, canUseReports, canUseShiftReport, isReadOnlyUser } from "./utils/roles";

import ScreeningForm from "./ScreeningForm";
import BirthResuscitation from "./BirthResuscitationForm";
import FormC from "./FormC";
import FormD from "./FormD";
import FormE from "./FormE";
import FormF from "./FormF";
import FormG from "./FormG";
import FormH from "./FormH";
import FormI from "./FormI";
import FormJ from "./FormJ";
import FormK from "./FormK";
import FormL from "./FormL";

import FiO2AUC from "./FiO2AUC";
import RespCVNeuroLog from "./RespCVNeuroLog";
import InfectGIHemaLog from "./InfectGIHemaLog";
import MetabRenalVascEyeLog from "./MetabRenalVascEyeLog";
import MinimalMonitoringLog from "./MinimalMonitoringLog";

import FormY_SAE from "./FormY_SAE";
import AdverseEventsForm from "./AdverseEventsForm";
import SeriousAdverseEventsList from "./SeriousAdverseEventsList";

import ViewEntries from "./ViewEntries";
import HelperFormRecords from "./HelperFormRecords";
import LogOfAllBirths from "./LogOfAllBirths";
import GACheckLog from "./GACheckLog";
import AuditTrail from "./AuditTrail";
import DataQueries from "./DataQueries";
import ReportBuilder from "./ReportBuilder";
import SiteSummary from "./SiteSummary";
import ShiftReport from "./ShiftReport";
import DataQueriesNavLink from "./components/DataQueriesNavLink";
import EditScreening from "./EditScreening";
import Dashboard from "./Dashboard";
import TrialMonitoringDashboard from "./TrialMonitoringDashboard";
import Login from "./Login";
import ChangePassword from "./ChangePassword";
import LandingPage from "./LandingPage";
import ManageStaff from "./ManageStaff";

import ProtectedRoute from "./components/ProtectedRoute";
import FloatingLogout from "./components/FloatingLogout";
import FormLayout from "./layouts/FormLayout";
import EnrollmentFormRedirect from "./components/EnrollmentFormRedirect";
import ScreeningFormRedirect from "./components/ScreeningFormRedirect";
import {
  ENROLLMENT_ID_FORM_REDIRECTS,
  SCREENING_ID_FORM_REDIRECTS,
} from "./config/formRouteRedirects";

import "./App.css";
import "./styles/UnifiedForms.css";

// Twins / triplets: a fresh Form B per baby (same page, new state).
function BirthResuscitationPerBaby() {
  const { screeningId, babyNumber } = useParams();
  return <BirthResuscitation key={`${screeningId}/${babyNumber || 1}`} />;
}


function AppContent() {
  const { token, user } = useAuth();
  const { resetProgress } = useFormProgress();
  const location = useLocation();

  const isLandingPage = location.pathname === "/";
  const isLoginPage   = location.pathname === "/login";
  const isChangePasswordPage = location.pathname === "/change-password";
  const isDashboardPage = location.pathname === "/dashboard";
  const isAuthChromePage = isLoginPage || isChangePasswordPage;
  const isGaCheckPage = location.pathname === "/ga-check";
  const isBirthLogPage = location.pathname === "/log-of-all-births";
  // Workspace links leave /dashboard. Do not re-show the pre-Stitch header/navbar.
  const isWorkspaceDest =
    location.pathname === "/entries" ||
    location.pathname === "/manage-staff" ||
    location.pathname === "/trial-monitoring" ||
    location.pathname === "/audit-trail" ||
    location.pathname === "/data-queries" ||
    location.pathname === "/reports" ||
    location.pathname === "/site-summary" ||
    location.pathname === "/shift-report";
  const usesDashboardLayout = isDashboardPage || isWorkspaceDest;

  const isFormPage =
    location.pathname.includes("/form-") ||
    location.pathname.includes("/fio2-") ||
    location.pathname.includes("/vs6-1") ||
    location.pathname.includes("/infect-") ||
    location.pathname.includes("/metab-") ||
    location.pathname.includes("/minimal-monitoring") ||
    location.pathname.includes("/adverse-") ||
    location.pathname.includes("/sae-");

  return (
    <div className={`app-container${isFormPage ? " form-page-layout" : ""}${isLandingPage ? " landing-page-layout" : ""}${usesDashboardLayout ? " dashboard-page-layout" : ""}`}>

      {/* ===== HEADER — hidden on landing, login, password change, forms, dashboard, workspace pages ===== */}
      {!isFormPage && !isLandingPage && !isAuthChromePage && !isDashboardPage && !isWorkspaceDest && (
        <header className="app-header">
          <div className="header-inner">

            {/* LEFT — PORTAL logo */}
            <div className="header-logo-panel">
              <div className="header-logo-box">
                <img src="/logo.png" alt="PORTAL Trial Logo" className="header-logo-img" />
              </div>
              <span className="header-logo-label">PORTAL</span>
            </div>

            {/* CENTER — Title */}
            <div className="header-titles">
              <h1>PORTAL Trial</h1>
              <p className="header-subtitle">
                Initial Oxygen for Delivery Room Resuscitation of Preterm Neonates
              </p>
              <p className="header-subtitle-small">
                A triple-arm, multisite, randomized, controlled trial
              </p>
              <div className="header-badges">
                <span>PGIMER Chandigarh</span>
                <span>ICMR Funded</span>
                <span className="badge-green">Multi-site RCT</span>
              </div>
            </div>

            {/* RIGHT — ICMR logo */}
            <div className="header-logo-panel">
              <div className="header-logo-box">
                <img src="/icmr-logo.jpg" alt="ICMR Logo" className="header-logo-img" />
              </div>
              <span className="header-logo-label">ICMR</span>
            </div>

          </div>
          {/* Research Staff Login button in header */}
          {!token && (
            <div className="header-login-bar">
              <a href="/login" className="header-login-btn">
                🔐 Research Staff Login
              </a>
            </div>
          )}
          <div className="header-glow-bar"></div>
        </header>
      )}

        {/* ===== NAVBAR — hidden on landing / login / password change / dashboard / workspace pages ===== */}
        {token && !isFormPage && !isLandingPage && !isAuthChromePage && !isDashboardPage && !isWorkspaceDest && (
          <nav className="nav-bar">
            <div className="nav-links">
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
              >
                <Home size={16} /> <span>Dashboard</span>
              </NavLink>

              <NavLink
                to="/form-a"
                className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                onClick={() => {
                  localStorage.removeItem("current_screening_id");
                  localStorage.removeItem("current_enrollment_id");
                  localStorage.removeItem("enrollment_locked");
                  localStorage.removeItem("enrollment_lock_reason");
                  window.dispatchEvent(new Event("storage"));
                  window.location.href = "/form-a";
                }}
              >
                <Plus size={16} /> <span>New Entry</span>
              </NavLink>

              {isBirthLogPage && (
                <NavLink
                  to="/ga-check"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <Search size={16} />
                  <span>Gestation (Inclusion Criteria) Screening Log</span>
                </NavLink>
              )}
              {isGaCheckPage && (
                <NavLink
                  to="/log-of-all-births"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <Hospital size={16} />
                  <span>Log of All Births</span>
                </NavLink>
              )}

              <NavLink
                to="/entries"
                className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
              >
                <ClipboardList size={16} /> <span>View Entries</span>
              </NavLink>

              {user?.role === "superadmin" && (
                <NavLink
                  to="/manage-staff"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <Users size={16} /> <span>Manage Staff</span>
                </NavLink>
              )}
              {canSeeDataQueries(user) && <DataQueriesNavLink />}
              {canUseReports(user) && (
                <NavLink
                  to="/reports"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <FileSpreadsheet size={16} /> <span>Reports</span>
                </NavLink>
              )}
              {canUseReports(user) && (
                <NavLink
                  to="/site-summary"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <HeartPulse size={16} /> <span>Site summary</span>
                </NavLink>
              )}
              {canUseShiftReport(user) && (
                <NavLink
                  to="/shift-report"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <ClipboardCheck size={16} /> <span>Twice-daily report</span>
                </NavLink>
              )}
              {canSeeAnalytics(user) && (
                <NavLink
                  to="/trial-monitoring"
                  className={({ isActive }) => `nav-btn ${isActive ? "active" : ""}`}
                >
                  <Activity size={16} /> <span>Trial Monitoring</span>
                </NavLink>
              )}
            </div>

            <div className="nav-right">
              <div className="nav-divider"></div>
              <FloatingLogout />
            </div>
          </nav>
        )}

        {isReadOnlyUser(user) && (
          <div
            role="status"
            style={{ background: "#eff6ff", color: "#1e3a8a", borderBottom: "1px solid #bfdbfe",
                     padding: "6px 16px", fontSize: 13, textAlign: "center" }}
          >
            View-only account: you can open every record, but entries cannot be created, changed or deleted.
          </div>
        )}

        {/* ===== ROUTES ===== */}
        <PatientProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route
              path="*"
              element={
                <main className={`app-main${usesDashboardLayout ? " dashboard-fullbleed" : ""}`}>
                  <div className="content-wrapper">
                    <Routes>
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/home" element={<Navigate to="/dashboard" />} />
                      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                      <Route path="/manage-staff" element={<ProtectedRoute><ManageStaff /></ProtectedRoute>} />
                      <Route path="/trial-monitoring" element={<ProtectedRoute><TrialMonitoringDashboard /></ProtectedRoute>} />
                      <Route path="/entries" element={<ProtectedRoute><ViewEntries /></ProtectedRoute>} />
                      <Route path="/helper-form-records" element={<ProtectedRoute><HelperFormRecords /></ProtectedRoute>} />
                      <Route path="/ga-check" element={<ProtectedRoute><GACheckLog /></ProtectedRoute>} />
                      <Route path="/log-of-all-births" element={<ProtectedRoute><LogOfAllBirths /></ProtectedRoute>} />
                      <Route path="/audit-trail" element={<ProtectedRoute><AuditTrail /></ProtectedRoute>} />
                      <Route path="/data-queries" element={<ProtectedRoute><DataQueries /></ProtectedRoute>} />
                      <Route path="/reports" element={<ProtectedRoute><ReportBuilder /></ProtectedRoute>} />
                      <Route path="/site-summary" element={<ProtectedRoute><SiteSummary /></ProtectedRoute>} />
                      <Route path="/shift-report" element={<ProtectedRoute><ShiftReport /></ProtectedRoute>} />
                      <Route path="/edit/:id" element={<ProtectedRoute><EditScreening /></ProtectedRoute>} />
                      <Route path="/form-a/:screeningId?" element={<ProtectedRoute><FormLayout currentForm="form_a"><ScreeningForm /></FormLayout></ProtectedRoute>} />
                      {SCREENING_ID_FORM_REDIRECTS.map(({ basePath, currentForm, label }) => (
                        <Route
                          key={basePath}
                          path={basePath}
                          element={
                            <ProtectedRoute>
                              <FormLayout currentForm={currentForm}>
                                <ScreeningFormRedirect basePath={basePath} formLabel={label} />
                              </FormLayout>
                            </ProtectedRoute>
                          }
                        />
                      ))}
                      <Route path="/form-b/:screeningId" element={<ProtectedRoute><FormLayout currentForm="form_b"><BirthResuscitationPerBaby /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-b/:screeningId/:babyNumber" element={<ProtectedRoute><FormLayout currentForm="form_b"><BirthResuscitationPerBaby /></FormLayout></ProtectedRoute>} />
                      {ENROLLMENT_ID_FORM_REDIRECTS.map(({ basePath, currentForm, label }) => (
                        <Route
                          key={basePath}
                          path={basePath}
                          element={
                            <ProtectedRoute>
                              <FormLayout currentForm={currentForm}>
                                <EnrollmentFormRedirect basePath={basePath} formLabel={label} />
                              </FormLayout>
                            </ProtectedRoute>
                          }
                        />
                      ))}
                      <Route path="/form-c/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_c"><FormC /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-d/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_d"><FormD /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-e/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_e"><FormE /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-f/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_f"><FormF /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-g/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_g"><FormG /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-h/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_h"><FormH /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-i/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_i"><FormI /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-j/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_j"><FormJ /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-k/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_k"><FormK /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-l/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_l"><FormL /></FormLayout></ProtectedRoute>} />
                      <Route path="/fio2-auc/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="fio2_auc"><FiO2AUC /></FormLayout></ProtectedRoute>} />
                      <Route path="/vs6-1/:enrollmentId?" element={<ProtectedRoute><FormLayout currentForm="vs6_1"><RespCVNeuroLog /></FormLayout></ProtectedRoute>} />
                      <Route path="/infect-gi-hema-log/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="infect_gi_hema"><InfectGIHemaLog /></FormLayout></ProtectedRoute>} />
                      <Route path="/metab-renal-vasc-eye-log/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="metab_renal_vasc_eye"><MetabRenalVascEyeLog /></FormLayout></ProtectedRoute>} />
                      <Route path="/minimal-monitoring/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="minimal_monitoring"><MinimalMonitoringLog /></FormLayout></ProtectedRoute>} />
                      <Route path="/form-y-sae/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="form_y_sae"><FormY_SAE /></FormLayout></ProtectedRoute>} />
                      <Route path="/adverse-events/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="adverse_events"><AdverseEventsForm /></FormLayout></ProtectedRoute>} />
                      <Route path="/sae-list/:enrollmentId" element={<ProtectedRoute><FormLayout currentForm="sae_list"><SeriousAdverseEventsList /></FormLayout></ProtectedRoute>} />
                    </Routes>
                  </div>
                </main>
              }
            />
          </Routes>
        </PatientProvider>

        {/* ===== FOOTER ===== */}
        {!isFormPage && !isLandingPage && !isAuthChromePage && !isDashboardPage && !isWorkspaceDest && (
          <footer className="app-footer">
            <p>© 2025 PORTAL Trial | Developed for Clinical Research Data Entry</p>
          </footer>
        )}

      </div>
  );
}


function App() {
  return (
    <Router>
      <FormProgressProvider>
        <ActiveFormSessionProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ActiveFormSessionProvider>
      </FormProgressProvider>
    </Router>
  );
}

export default App;
